import { z } from 'zod';
import { aiProviderSchema } from './env.js';
import { SUPPORTED_LOCALES } from './locales.js';
const localeSchema = z.enum(SUPPORTED_LOCALES);
const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const starterQuestionInputSchema = z
    .object({
    question: z.string().trim().min(1).max(160),
    answer: z.string().trim().max(2000).optional(),
    icon: z.enum(['QUESTION', 'INFO', 'SERVICES', 'PRICE', 'CLOCK', 'CONTACT']).optional(),
    action: z.enum(['ANSWER', 'CONTACT', 'QUOTE']).optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if ((value.action ?? 'ANSWER') === 'ANSWER' && !value.answer) {
        ctx.addIssue({
            code: 'custom',
            path: ['answer'],
            message: 'Prepared answer is required for answer actions',
        });
    }
});
export const botLocaleInputSchema = z
    .object({
    locale: localeSchema,
    enabled: z.boolean(),
    greeting: z.string().trim().min(1).max(500),
    fallback: z.string().trim().min(1).max(500),
    starterQuestions: z.array(starterQuestionInputSchema).max(6).optional(),
    labels: z.record(z.string().max(40), z.string().max(80)).optional(),
})
    .strict();
export const botSettingsInputSchema = z
    .object({
    colors: z.object({ primary: hexColorSchema.optional() }).strict().optional(),
    position: z.enum(['left', 'right']).optional(),
    contactUrl: z.url().max(2000).nullable().optional(),
    privacyUrl: z.url().max(2000).nullable().optional(),
    noticeTexts: z.record(localeSchema, z.string().max(500)).optional(),
    noticeVersion: z.string().trim().min(1).max(32).optional(),
    leadNotifyEmail: z.email().max(320).nullable().optional(),
    businessInstructions: z.string().max(4000).nullable().optional(),
    historyRetentionDays: z.number().int().min(1).max(3650).optional(),
})
    .strict();
export const updateBotRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    enabled: z.boolean().optional(),
    defaultLocale: localeSchema.optional(),
    locales: z.array(botLocaleInputSchema).max(3).optional(),
    settings: botSettingsInputSchema.optional(),
})
    .strict();
export const createOriginRequestSchema = z
    .object({
    exactOrigin: z.string().trim().min(1).max(255),
})
    .strict();
export const issuePairingResponseSchema = z.object({
    expiresAt: z.iso.datetime(),
    code: z.string().min(20),
});
export const pairRequestSchema = z
    .object({
    code: z.string().min(20).max(200),
    origin: z.string().trim().min(1).max(255),
    installNonce: z.string().min(16).max(200),
    wpVersion: z.string().max(32).optional(),
    pluginVersion: z.string().max(32).optional(),
})
    .strict();
export const pairResponseSchema = z.object({
    installationId: z.uuid(),
    publicSiteId: z.string(),
    credential: z.string(),
    keyVersion: z.number().int(),
});
export const heartbeatRequestSchema = z
    .object({
    wpVersion: z.string().max(32).optional(),
    pluginVersion: z.string().max(32).optional(),
})
    .strict();
export const planLimitsSchema = z
    .object({
    sites: z.number().int().min(0).max(10_000),
    monthlyAnswers: z.number().int().min(0).max(10_000_000),
    siteMonthlyAnswers: z.number().int().min(0).max(10_000_000).nullable().optional(),
    maxConcurrent: z.number().int().min(1).max(1_000).optional(),
    spendingCeilingUsd: z.number().min(0).max(1_000_000).nullable().optional(),
    ingestionMonthlyTokens: z.number().int().min(0).max(100_000_000).optional(),
    maxSources: z.number().int().min(0).max(100_000).optional(),
    maxPdfBytes: z
        .number()
        .int()
        .min(1)
        .max(50 * 1024 * 1024)
        .optional(),
    maxExtractedChars: z.number().int().min(1).max(50_000_000).optional(),
    maxInputTokens: z.number().int().min(1).max(128_000).optional(),
    maxOutputTokens: z.number().int().min(1).max(16_000).optional(),
})
    .strict();
const monthlyPriceCentsSchema = z.number().int().min(0).max(100_000_000);
export const createPlanRequestSchema = z
    .object({
    code: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9-]+$/),
    name: z.string().trim().min(1).max(200),
    limits: planLimitsSchema,
    monthlyPriceCents: monthlyPriceCentsSchema.optional(),
    currency: z.literal('EUR').optional(),
})
    .strict();
export const updatePlanRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    limits: planLimitsSchema.optional(),
    monthlyPriceCents: monthlyPriceCentsSchema.optional(),
})
    .strict();
export const updateInvoiceRequestSchema = z
    .object({
    status: z.enum(['OPEN', 'VOID']),
    paymentNote: z.string().trim().max(200).nullable().optional(),
})
    .strict();
export const createManualInvoiceRequestSchema = z
    .object({
    tenantId: z.uuid(),
    amountCents: z.number().int().min(1).max(100_000_000),
    periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    dueAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
})
    .strict();
export const recordInvoicePaymentRequestSchema = z
    .object({
    amountCents: z.number().int().min(1).max(100_000_000),
    paidOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    note: z.string().trim().max(500).nullable().optional(),
})
    .strict();
export const assignSubscriptionRequestSchema = z
    .object({
    planCode: z.string().trim().min(1).max(64),
})
    .strict();
export const updatePlatformSettingsRequestSchema = z
    .object({
    provider: aiProviderSchema,
})
    .strict();
export const updatePlatformSmtpRequestSchema = z
    .object({
    host: z.string().trim().min(1).max(255),
    port: z.number().int().min(1).max(65535),
    user: z.string().trim().min(1).max(320).nullable(),
    password: z.string().min(1).max(500).nullable().optional(),
    clearPassword: z.boolean().optional(),
    mailFrom: z.email().max(320),
})
    .strict();
export const testPlatformSmtpRequestSchema = z
    .object({
    to: z.email().max(320),
})
    .strict();
export const applicationSettingValueTypeSchema = z.enum(['TEXT', 'INT', 'FLOAT', 'BOOL', 'JSON']);
export const confirmLeadNotifyRequestSchema = z
    .object({
    token: z.string().min(20).max(200),
})
    .strict();
export const emailBodyFormatSchema = z.enum(['TEXT', 'HTML']);
export const updateEmailTemplateRequestSchema = z
    .object({
    format: emailBodyFormatSchema,
    subject: z.string().trim().min(1).max(200),
    textBody: z.string().min(1).max(20_000),
    htmlBody: z.string().max(50_000),
    enabled: z.boolean(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.format === 'HTML' && value.htmlBody.trim().length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['htmlBody'],
            message: 'HTML body is required when format is HTML',
        });
    }
});
export const updateApplicationSettingsRequestSchema = z
    .object({
    items: z
        .array(z
        .object({
        key: z.string().trim().min(1).max(80),
        value: z.string().max(20_000),
    })
        .strict())
        .min(1)
        .max(100),
})
    .strict();
export const updateSubscriptionRequestSchema = z
    .object({
    status: z.enum(['ACTIVE', 'ENDED', 'SUSPENDED']).optional(),
    endsAt: z.iso.datetime().nullable().optional(),
    limitsOverride: planLimitsSchema.nullable().optional(),
})
    .strict();
//# sourceMappingURL=platform.js.map